import {
  Badge,
  Button,
  Card,
  Text,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
  Spinner,
} from "@fluentui/react-components";
import {
  ArrowLeft24Regular,
  Checkmark24Regular,
  DocumentMultiple24Regular,
  DocumentTable24Regular,
  PeopleTeam24Regular,
  Send24Regular,
} from "@fluentui/react-icons";
import React, { useState } from "react";
import { Page } from "../components/App";
import { useJob } from "../context/JobContext";
import { useMsal } from "@azure/msal-react";
import { submitJobRequest } from "../../authConfig";
import { submitJob } from "../services/jobService";
import { MemberExtractionJobRequest, SubmitJobRequest } from "../types";
import { taskPaneBackground } from "../theme";
import { getFieldSelectionSummary, partitionSelectedFields } from "./confirmationState";

const MEMBER_METHOD_LABELS: Record<string, string> = {
  AI_DETECT: "AI detection",
  CSV: "CSV upload",
  MANUAL: "Manual selection",
};

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalM),
    boxSizing: "border-box",
    height: "88vh",
    ...taskPaneBackground,
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalL),
    overflow: "hidden",
  },
  headerCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius("14px"),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalXL),
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    display: "flex",
    flexDirection: "column",
    // Tight header: remove default h1/p margins and use a small explicit gap.
    ...shorthands.gap("2px"),
    flexShrink: 0,
  },
  headerTitle: {
    // `Text as="h1"` renders a real <h1> with default margins unless reset.
    margin: 0,
  },
  headerSubtitle: {
    // `Text as="p"` renders a real <p> with default margins unless reset.
    margin: 0,
    color: tokens.colorNeutralForeground3,
  },
  headerEyebrow: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap(tokens.spacingHorizontalXS),
    color: "#0f6cbd",
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: tokens.spacingVerticalXXS,
  },
  summaryScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalM),
    paddingRight: tokens.spacingHorizontalXXS,
  },
  readinessCard: {
    flexShrink: 0,
    backgroundColor: "#eff6ff",
    ...shorthands.border("1px", "solid", "#bfdbfe"),
    ...shorthands.borderRadius("14px"),
    ...shorthands.padding(tokens.spacingVerticalM),
    boxShadow: "none",
  },
  readinessHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    ...shorthands.gap(tokens.spacingHorizontalS),
    marginBottom: tokens.spacingVerticalM,
  },
  readinessTitle: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap(tokens.spacingHorizontalS),
    color: "#123b63",
    fontWeight: tokens.fontWeightSemibold,
  },
  readyIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    color: "#ffffff",
    backgroundColor: "#0f6cbd",
    ...shorthands.borderRadius("9px"),
  },
  metrics: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  metric: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("2px"),
    backgroundColor: "rgba(255, 255, 255, 0.72)",
    ...shorthands.border("1px", "solid", "#dbeafe"),
    ...shorthands.borderRadius("10px"),
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalS),
    minWidth: 0,
  },
  metricValue: {
    color: "#0f3b66",
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
  },
  metricLabel: {
    color: "#4b6480",
    fontSize: tokens.fontSizeBase200,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  sectionCard: {
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius("14px"),
    ...shorthands.padding(tokens.spacingVerticalM),
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap(tokens.spacingHorizontalS),
    marginBottom: tokens.spacingVerticalM,
  },
  sectionIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "34px",
    height: "34px",
    flexShrink: 0,
    color: "#0f6cbd",
    backgroundColor: "#eff6ff",
    ...shorthands.border("1px", "solid", "#dbeafe"),
    ...shorthands.borderRadius("9px"),
  },
  sectionHeading: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  sectionTitle: {
    fontWeight: tokens.fontWeightSemibold,
  },
  sectionCaption: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  documentList: {
    listStyleType: "none",
    ...shorthands.padding(0),
    ...shorthands.margin(0),
    maxHeight: "180px",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalXS),
  },
  documentItem: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap(tokens.spacingHorizontalS),
    minWidth: 0,
    backgroundColor: "#f8fafc",
    ...shorthands.border("1px", "solid", "#e2e8f0"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
  },
  documentName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  configurationGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    ...shorthands.gap(tokens.spacingHorizontalS),
    "@media (max-width: 420px)": {
      gridTemplateColumns: "1fr",
    },
  },
  configurationCard: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalS),
    backgroundColor: "#f8fafc",
    ...shorthands.border("1px", "solid", "#e2e8f0"),
    ...shorthands.borderRadius("10px"),
    ...shorthands.padding(tokens.spacingVerticalM),
    minWidth: 0,
  },
  configurationHeader: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap(tokens.spacingHorizontalS),
    color: "#0f3b66",
    fontWeight: tokens.fontWeightSemibold,
  },
  configurationDetail: {
    color: tokens.colorNeutralForeground3,
  },
  rule: {
    color: tokens.colorNeutralForeground2,
    backgroundColor: "#ffffff",
    ...shorthands.borderLeft("3px", "solid", "#93c5fd"),
    ...shorthands.borderRadius("4px"),
    ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalS),
    fontStyle: "italic",
    overflowWrap: "anywhere",
  },
  footerCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius("14px"),
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalXL),
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    flexShrink: 0,
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  submissionError: {
    width: "100%",
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.border("1px", "solid", "#d13438"),
    backgroundColor: "#fde7e9",
    color: "#8a1c1f",
    boxSizing: "border-box",
  },
  footerActions: {
    display: "flex",
    ...shorthands.gap(tokens.spacingHorizontalS),
    flexWrap: "nowrap", // Keep Back/Submit horizontally aligned; labels shorten on narrow panes.
    width: "100%",
    justifyContent: "space-between",
  },
  footerButton: {
    minHeight: "44px",
    whiteSpace: "nowrap",
    textAlign: "center",
    minWidth: 0,
  },
  footerLabelLong: {
    display: "inline",
    "@media (max-width: 520px)": {
      display: "none",
    },
  },
  footerLabelShort: {
    display: "none",
    "@media (max-width: 520px)": {
      display: "inline",
    },
  },
});

export const ConfirmationPage: React.FC<{ onNavigate: (page: Page) => void }> = ({
  onNavigate,
}) => {
  const classes = useStyles();
  const { state, dispatch } = useJob();
  const { instance, accounts } = useMsal();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const {
    sourceFolderUrl,
    sourceFolderDriveId,
    sourceFolderItemId,
    selectedFiles,
    memberConfig,
    fieldConfig,
  } = state;
  const memberMethodLabel =
    MEMBER_METHOD_LABELS[memberConfig.method] ?? memberConfig.method.replace(/_/g, " ");
  const memberCountLabel =
    memberConfig.method === "AI_DETECT" ? "Automatic" : memberConfig.selectedMembers.length;
  const fieldSummary = getFieldSelectionSummary(
    fieldConfig.selectedFields,
    fieldConfig.customFields.map(({ value }) => value)
  );
  const partitionedFields = partitionSelectedFields(
    fieldConfig.selectedFields,
    fieldConfig.customFields.map(({ value }) => value)
  );

  const handleSubmitJob = async () => {
    setSubmissionError(null);
    setIsSubmitting(true);
    try {
      let tokenResponse;
      try {
        tokenResponse = await instance.acquireTokenSilent({
          ...submitJobRequest,
          account: accounts[0],
        });
      } catch (_error: any) {
        // If we haven't yet consented to `offline_access` (or interaction is required),
        // fall back to an interactive popup.
        tokenResponse = await instance.acquireTokenPopup({
          ...submitJobRequest,
          account: accounts[0],
        } as any);
      }

      const memberExtractionConfig: MemberExtractionJobRequest = {
        source_folder_url: sourceFolderUrl,
        source_folder_drive_id: sourceFolderDriveId,
        source_folder_item_id: sourceFolderItemId,
        selected_files: selectedFiles,
        member_config: {
          method: memberConfig.method === "CSV" ? "MANUAL" : memberConfig.method,
          selected_members: memberConfig.selectedMembers,
          custom_rule: memberConfig.customRule,
        },
        field_config: {
          selected_fields: partitionedFields.standardFields,
          custom_fields: partitionedFields.customFields,
          custom_rule: fieldConfig.customRule,
        },
      };
      const jobRequest: SubmitJobRequest = {
        job_type: "MEMBER_EXTRACTION",
        job_config: memberExtractionConfig,
      };

      // We intentionally avoid logging the job payload (may contain sensitive URLs / filenames).
      const result = await submitJob(jobRequest, tokenResponse.accessToken);
      console.log("Job submitted successfully. batch_id=", result?.batch_id);

      // Set the active batch ID in the global state
      dispatch({ type: "SET_CURRENT_BATCH_ID", payload: result.batch_id });

      // Navigate to the dashboard to monitor progress
      onNavigate("dashboard");
    } catch (error) {
      console.error("Failed to submit job:", error);
      setSubmissionError(
        "We couldn't submit your job right now. Please try again in a moment. " +
          "If this keeps happening, refresh the add-in and sign in again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.headerCard}>
        <div className={classes.headerEyebrow}>Member data extraction · Final step</div>
        <Text as="h1" weight="bold" size={600} className={classes.headerTitle}>
          Review and submit
        </Text>
        <Text as="p" size={300} className={classes.headerSubtitle}>
          Check the extraction scope before starting the job.
        </Text>
      </div>

      <div className={classes.summaryScroll}>
        <Card className={classes.readinessCard}>
          <div className={classes.readinessHeader}>
            <div className={classes.readinessTitle}>
              <span className={classes.readyIcon}>
                <Checkmark24Regular />
              </span>
              <Text size={400} weight="semibold">
                Extraction ready
              </Text>
            </div>
            <Badge appearance="filled" color="brand" size="small">
              Ready
            </Badge>
          </div>
          <div className={classes.metrics}>
            <div className={classes.metric}>
              <Text className={classes.metricValue}>{selectedFiles.length}</Text>
              <Text className={classes.metricLabel}>
                {selectedFiles.length === 1 ? "File" : "Files"}
              </Text>
            </div>
            <div className={classes.metric}>
              <Text className={classes.metricValue}>{memberCountLabel}</Text>
              <Text className={classes.metricLabel}>Members</Text>
            </div>
            <div className={classes.metric}>
              <Text className={classes.metricValue}>{fieldSummary.total}</Text>
              <Text className={classes.metricLabel}>Fields</Text>
            </div>
          </div>
        </Card>

        <Card className={classes.sectionCard}>
          <div className={classes.sectionHeader}>
            <span className={classes.sectionIcon}>
              <DocumentMultiple24Regular />
            </span>
            <div className={classes.sectionHeading}>
              <Text className={classes.sectionTitle}>Documents</Text>
              <Text className={classes.sectionCaption}>
                {selectedFiles.length} selected for processing
              </Text>
            </div>
          </div>
          <ul className={classes.documentList}>
            {selectedFiles.map((file) => (
              <li key={file.url} className={classes.documentItem} title={file.name}>
                <DocumentMultiple24Regular color="#0f6cbd" />
                <Text className={classes.documentName}>{file.name}</Text>
              </li>
            ))}
          </ul>
        </Card>

        <Card className={classes.sectionCard}>
          <div className={classes.sectionHeader}>
            <span className={classes.sectionIcon}>
              <DocumentTable24Regular />
            </span>
            <div className={classes.sectionHeading}>
              <Text className={classes.sectionTitle}>Extraction configuration</Text>
              <Text className={classes.sectionCaption}>How members and fields will be handled</Text>
            </div>
          </div>

          <div className={classes.configurationGrid}>
            <div className={classes.configurationCard}>
              <div className={classes.configurationHeader}>
                <PeopleTeam24Regular />
                <Text weight="semibold">Members</Text>
              </div>
              <Badge appearance="tint" color="brand">
                {memberMethodLabel}
              </Badge>
              <Text className={classes.configurationDetail}>
                {memberConfig.method === "AI_DETECT"
                  ? "Members will be identified automatically."
                  : `${memberConfig.selectedMembers.length} member${
                      memberConfig.selectedMembers.length === 1 ? "" : "s"
                    } selected.`}
              </Text>
              {memberConfig.customRule && (
                <Text className={classes.rule}>“{memberConfig.customRule}”</Text>
              )}
            </div>

            <div className={classes.configurationCard}>
              <div className={classes.configurationHeader}>
                <DocumentTable24Regular />
                <Text weight="semibold">Report fields</Text>
              </div>
              <Badge appearance="tint" color="brand">
                {fieldSummary.total} configured
              </Badge>
              <Text className={classes.configurationDetail}>
                {fieldSummary.standard} standard
                {fieldSummary.custom > 0 ? ` · ${fieldSummary.custom} custom` : ""}
              </Text>
              {fieldConfig.customRule && (
                <Text className={classes.rule}>“{fieldConfig.customRule}”</Text>
              )}
            </div>
          </div>
        </Card>
      </div>

      <div className={classes.footerCard}>
        {submissionError && (
          <Text role="alert" className={classes.submissionError}>
            {submissionError}
          </Text>
        )}
        <div className={classes.footerActions}>
          <Button
            appearance="primary"
            icon={<ArrowLeft24Regular />}
            className={mergeClasses(classes.footerButton, "sourcestone-navigation-primary")}
            onClick={() => onNavigate("fieldSelection")}
            disabled={isSubmitting}
          >
            <span className={classes.footerLabelLong}>Back to Field Selection</span>
            <span className={classes.footerLabelShort}>Back</span>
          </Button>
          <Button
            appearance="primary"
            icon={isSubmitting ? <Spinner size="tiny" /> : <Send24Regular />}
            onClick={handleSubmitJob}
            className={mergeClasses(classes.footerButton, "sourcestone-navigation-primary")}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              "Submitting..."
            ) : (
              <>
                <span className={classes.footerLabelLong}>Submit Job</span>
                <span className={classes.footerLabelShort}>Submit</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
